/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnInit } from '@angular/core';
import { AuthenticationService, Logger } from '@app/core';
import { HotelsService } from '@app/hotels/hotels/hotels.service';
import { HotelsFormService } from '@app/hotels/hotels/hotels-form.service';
import { DataStateChangeEvent, GridDataResult, SelectableSettings } from '@progress/kendo-angular-grid';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { ApiQuery } from '@app/core/http/api-query';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { HotelsSeoFormService } from '@app/hotels/hotels/hotels-seo-form.service';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { MapModalService } from '@app/shared/components/map-modal/map-modal.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { environment } from '@env/environment';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { AccessService } from '@app/core/access/access.service';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';

const log = new Logger('hotelsListComponent');

@Component({
  selector: 'app-hotels-list',
  templateUrl: './hotels-list.component.html',
  styleUrls: ['./hotels-list.component.scss'],
})
export class HotelsListComponent extends KendoGridComponent implements OnInit {
  hotelsResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  LaravelModels = LaravelModelsEnum;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;
  selectableSettings: SelectableSettings = {
    checkboxOnly: true,
    mode: 'multiple',
  };
  public selectedHotels: Array<number> = [];
  form: FormGroup;
  env = environment;
  AuthenticationService = AuthenticationService;
  UserRoleType = UserRoleType;

  constructor(
    private hotelService: HotelsService,
    private hotelFormService: HotelsFormService,
    protected kendoGridService: KendoGridService,
    private hotelsSeoFormService: HotelsSeoFormService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private mapModalService: MapModalService,
    private formBuilder: FormBuilder,
    public accessService: AccessService
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    // if (window.sessionStorage.getItem('HotelsListComponent')) {
    //   this.state = JSON.parse(window.sessionStorage.getItem('HotelsListComponent'));
    // }

    this.state = CustomKendoFilter.getStateFromStorage('HotelsListComponent');

    this.createForm();
    this.loadData(this.state);
  }

  test() {
    log.debug(this.selectedHotels);
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null, venueId: number = null, keywords: string = '') {
    if (!venueId && state && state.filter) {
      state.filter.filters.forEach((filter: any, key: number) => {
        if (filter['field'] == 'venue.distance') {
          state.filter.filters.splice(key, 1);
        }
      });

      state.sort.forEach((filter: any, key: number) => {
        if (filter['field'] == 'venue.distance') {
          state.sort.splice(key, 1);
        }
      });
    }
    if (keywords && keywords !== this.apiQuery['_lastKeyword']) {
      if (!state) {
        state = this.state;
      }
      if (state) {
        state.skip = 0;
      }
    }
    this.state = state;
    this.apiQuery.setDataSetFilters(state).addIncludes('city.country', 'type', 'venue', 'contacts.type', 'tags');

    this.apiQuery.removeFilter('venue_id');
    if (venueId) {
      this.apiQuery.addFilter('venue_id', venueId);
    }

    this.apiQuery.removeFilter('keywords');
    if (keywords) {
      this.apiQuery.addFilter('keywords', keywords);
    }
    this.apiQuery.handleKeyword(keywords);
    this.hotelService.index(this.apiQuery).subscribe((response: PaginatedResponse) => {
      this.hotelsResponse = response;
      this.data = response.getGridData();
    });
    //  window.sessionStorage.setItem('HotelsListComponent', JSON.stringify(this.state));
    CustomKendoFilter.pushStateInStorage('HotelsListComponent', this.state);
  }

  add() {
    this.hotelFormService.open().then(
      (response) => {
        if (response) {
          this.loadData(this.state, this.form.get('venue_id').value, this.form.get('keywords').value); // Reload data after adding a new hotel
        }
      },
      (err) => {
        log.info('modal closed without adding');
      }
    );
  }

  edit(hotel: Hotel): void {
    this.hotelFormService.open(hotel).then(
      (response) => {
        if (response) {
          this.loadData(this.state, this.form.get('venue_id').value, this.form.get('keywords').value); // Reload data after editing the hotel
        }
      },
      (err) => {
        log.info('modal closed without saving changes');
      }
    );
  }

  /**
   *  Отваря SEO формата
   * @param hotel Hotel
   */
  editSeo(hotel: Hotel): void {
    this.hotelsSeoFormService.open(hotel);
  }

  /**
   * @param hotel Hotel object
   */
  delete(hotel: Hotel): void {
    this.hotelService.destroy(hotel.id).subscribe(() => {
      this.loadData(this.state, this.form.get('venue_id').value, this.form.get('keywords').value);
    });
  }

  showOnMap() {
    if (this.selectedHotels.length <= 0 && !this.form.get('venue_id').value) {
      return this.translate.get('HOTELS.HOTELS_LIST.NO_HOTELS_TO_SHOW').subscribe((trans: string) => {
        this.toastr.warning(trans);
      });
    }
    let venues: Array<number> = [];
    if (this.form.get('venue_id').value) {
      venues = [Number(this.form.get('venue_id').value)];
    }

    this.mapModalService.open(this.selectedHotels, venues,  {
      width: 'auto',
      margin: '0 auto',
      position: 'absolute',
      left: '40%',
      top: '7%',
    }).then(
      () => {},
      (res: any) => {
        log.debug(res);
      }
    );
  }

  exportEmails() {
    const query = new ApiQuery();
    if (this.selectedHotels.length > 0) {
      query.addFilter('id', this.selectedHotels.toString());
    }
    this.hotelService.export(query).subscribe((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.ms-excel;charset=utf-8',
      });
      FileSaver.saveAs(blob, 'hotels-export-' + moment().format('DD_MM_YYYY_HH_mm_ss') + '.xlsx');
    });
  }

  clearFilters() {
    this.apiQuery = new ApiQuery();
    this.form.reset();
    this.loadData();
  }

  onSelectionChange(event): void {
    const newlySelectedIds = event.selectedRows.map(row => row.dataItem.id);
    const deselectedIds = event.deselectedRows.map(row => row.dataItem.id);

    this.selectedHotels = this.selectedHotels.filter(id => !deselectedIds.includes(id));

    for (const id of newlySelectedIds) {
      if (!this.selectedHotels.includes(id)) {
        this.selectedHotels.push(id);
      }
    }
  }

  private createForm() {
    this.form = this.formBuilder.group({
      venue_id: [null],
      keywords: [''],
    });
  }
}
